import { useState } from 'react';
import { ARTS, sortArts } from '../data/arts';
import type { Art } from '../types';

interface ArtPickerProps {
  /** Currently selected arts; empty = first-run gate (no close button). */
  current: Art[];
  onPick: (arts: Art[]) => void;
  /** Present only in switcher mode — renders a close button. */
  onClose?: () => void;
}

// The art gate. Shown full-screen before anything else on first run (App.tsx
// renders it instead of AppShell until arts are chosen), and again as an overlay
// from Settings or the Library header chip.
//
// One question, one column of choices, one button pinned at the bottom — it's
// the first thing a new user sees, so nothing else competes for attention. Each
// row carries its own checkbox so it reads as multi-select before you tap.
// Selection is a local draft, committed on the button, so toggling doesn't
// rebuild the technique graph on every tap.
export default function ArtPicker({ current, onPick, onClose }: ArtPickerProps) {
  const [draft, setDraft] = useState<Art[]>(current);

  const toggle = (a: Art) => {
    setDraft((prev) => (prev.includes(a) ? prev.filter((x) => x !== a) : sortArts([...prev, a])));
  };

  return (
    <div className="art-picker">
      {onClose && (
        <button className="art-picker-close" onClick={onClose} aria-label="Close">
          ✕
        </button>
      )}
      {/* Header band over a scrolling body — the same split every main view
          uses (ViewHeader + page-view-body), just centred since this screen has
          no tab bar or back arrow to balance against. */}
      <header className="art-picker-header">
        <div className="art-picker-logo">
          {/* Gnosis mark (the favicon node-cluster), same shape Home uses. */}
          <svg width={38} height={38} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <circle cx="12" cy="12" r="4" opacity="0.9" />
            <circle cx="6" cy="7" r="2.5" opacity="0.55" />
            <circle cx="18" cy="7" r="2" opacity="0.5" />
            <circle cx="5" cy="16" r="1.5" opacity="0.45" />
            <circle cx="19" cy="15" r="3" opacity="0.6" />
            <circle cx="12" cy="4" r="1.5" opacity="0.45" />
            <circle cx="8" cy="19" r="2" opacity="0.55" />
          </svg>
        </div>
        <h1 className="art-picker-title">What do you train?</h1>
        <p className="art-picker-sub">Pick one or more. Change it any time.</p>
      </header>

      <div className="art-picker-body">
        <div className="art-picker-grid" role="group" aria-label="Sports">
          {ARTS.map((a) => {
            const selected = draft.includes(a.id);
            return (
              <button
                key={a.id}
                type="button"
                role="checkbox"
                aria-checked={selected}
                className={`art-tile${selected ? ' art-tile--on' : ''}`}
                onClick={() => toggle(a.id)}
              >
                <span className="art-tile-name">{a.label}</span>
                <span className="art-tile-check" aria-hidden="true">✓</span>
              </button>
            );
          })}
        </div>

        {/* Invisible until something is picked, but always mounted — hiding it
            with `visibility` keeps its space reserved so the choices above
            don't jump when it appears. */}
        <button
          className={`art-picker-continue${draft.length === 0 ? ' art-picker-continue--hidden' : ''}`}
          disabled={draft.length === 0}
          onClick={() => onPick(draft)}
        >
          {onClose ? 'Done' : 'Continue'}
        </button>
      </div>
    </div>
  );
}
